import { useEffect, useId, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import {
  RiAlignCenter,
  RiAlignLeft,
  RiAlignRight,
  RiScissorsLine
} from "react-icons/ri";
import { normalizeEditorHtmlForTipTap } from "../../helpers/interventionHtml";

const cn = (...classes) => classes.filter(Boolean).join(" ");

const DEFAULT_MIN_HEIGHT = "120px";

const TOOLBAR_ACTIONS = [
  {
    key: "cut",
    label: (
      <RiScissorsLine className="h-4 w-4" />
    ),
    title: "Cut",
    isActive: () => false,
    command: (editor) => {
      editor.chain().focus().run();
      const didCut = document.execCommand("cut");
      if (!didCut) {
        editor.chain().focus().deleteSelection().run();
      }
    },
  },
  {
    key: "bold",
    label: <span className="font-bold">B</span>,
    title: "Bold",
    isActive: (editor) => editor.isActive("bold"),
    command: (editor) => editor.chain().focus().toggleBold().run(),
  },
  {
    key: "underline",
    label: <span className="underline">U</span>,
    title: "Underline",
    isActive: (editor) => editor.isActive("underline"),
    command: (editor) => editor.chain().focus().toggleUnderline().run(),
  },
  {
    key: "italic",
    label: <span className="italic">I</span>,
    title: "Italic",
    isActive: (editor) => editor.isActive("italic"),
    command: (editor) => editor.chain().focus().toggleItalic().run(),
  },
  {
    key: "strike",
    label: <span className="line-through">S</span>,
    title: "Strikethrough",
    isActive: (editor) => editor.isActive("strike"),
    command: (editor) => editor.chain().focus().toggleStrike().run(),
  },
  {
    key: "alignLeft",
    label: <RiAlignLeft className="h-4 w-4" />,
    title: "Align left",
    isActive: (editor) => editor.isActive({ textAlign: "left" }),
    command: (editor) => editor.chain().focus().setTextAlign("left").run(),
  },
  {
    key: "alignCenter",
    label: <RiAlignCenter className="h-4 w-4" />,
    title: "Align center",
    isActive: (editor) => editor.isActive({ textAlign: "center" }),
    command: (editor) => editor.chain().focus().setTextAlign("center").run(),
  },
  {
    key: "alignRight",
    label: <RiAlignRight className="h-4 w-4" />,
    title: "Align right",
    isActive: (editor) => editor.isActive({ textAlign: "right" }),
    command: (editor) => editor.chain().focus().setTextAlign("right").run(),
  },
];

const ToolbarButton = ({ active, onClick, label, title, tooltipId }) => (
  <button
    type="button"
    data-tooltip-id={tooltipId}
    data-tooltip-content={title}
    aria-label={title}
    aria-pressed={active}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={cn(
      "inline-flex items-center justify-center rounded px-2 py-0.5 text-sm font-medium leading-none",
      active ? "bg-teal-25 text-gray-800" : "text-teal-300 hover:bg-gray-100"
    )}
  >
    {label}
  </button>
);

const EditorToolbar = ({ editor, plain, tooltipId }) => {
  if (!editor) return null;

  return (
    <>
      <div
        className={cn(
          "flex flex-wrap items-center justify-end gap-0.5",
          plain
            ? "py-0.5"
            : "border-b border-gray-200 bg-gray-10 py-1 pl-1 pr-0.5"
        )}
        role="toolbar"
        aria-label="Text formatting"
      >
        {TOOLBAR_ACTIONS.map((action) => (
          <ToolbarButton
            key={action.key}
            tooltipId={tooltipId}
            active={action.isActive(editor)}
            onClick={() => action.command(editor)}
            label={action.label}
            title={action.title}
          />
        ))}
      </div>

      <Tooltip
        id={tooltipId}
        variant="light"
        noArrow
        opacity={1}
        className="!z-[100] !max-w-xs !rounded-md !border !border-gray-100 !bg-gray-10 !px-2 !py-1 !text-xs !font-medium !text-teal-300 !shadow-md"
      />
    </>
  );
};

const PharmacistTipTapEditor = ({
  value,
  onChange,
  disabled = false,
  minHeight = DEFAULT_MIN_HEIGHT,
  maxHeight,
  className,
  editorWrapClassName,
  headerTitle,
  headerClassName,
  toolbarPortalContainer,
  hideInlineToolbar = false,
  hideOuterBorder = false,
  ariaLabel = "Rich text editor",
}) => {
  const tooltipId = useId().replace(/:/g, "");
  const skipSyncRef = useRef(false);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Underline,
      TextAlign.configure({
        types: ["paragraph", "listItem"],
      }),
    ],
    []
  );

  const editor = useEditor({
    immediatelyRender: false,
    extensions,
    editable: !disabled,
    content: normalizeEditorHtmlForTipTap(value),
    editorProps: {
      attributes: {
        "aria-label": ariaLabel,
        class: cn(
          "prose prose-sm max-w-none focus:outline-none px-2 py-2 text-[16px] leading-snug text-gray-800",
          "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5",
          "[&_u]:underline",
          "[&_p]:!my-0 [&_li]:!my-0 [&_li>p]:!my-0",
          "[&_ul]:!my-1 [&_ol]:!my-1",
          editorWrapClassName
        ),
      },
    },
    onUpdate: ({ editor }) => {
      skipSyncRef.current = true;
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) return;

    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }

    const next = normalizeEditorHtmlForTipTap(value);
    if (next !== editor.getHTML()) {
      editor.commands.setContent(next, false);
    }
  }, [value, editor]);

  const sizingClass = "flex flex-col overflow-y-auto";
  const sizingStyle = useMemo(
    () => ({
      minHeight,
      ...(maxHeight ? { maxHeight } : {}),
    }),
    [minHeight, maxHeight]
  );

  const outerClass = hideOuterBorder
    ? "border-0"
    : "rounded border border-gray-200";

  if (!editor) {
    return (
      <div className={cn("bg-white", outerClass, sizingClass)} style={sizingStyle} />
    );
  }

  const showToolbar = !hideInlineToolbar && !toolbarPortalContainer;

  const portalToolbar =
    toolbarPortalContainer &&
    createPortal(
      <EditorToolbar editor={editor} plain tooltipId={tooltipId} />,
      toolbarPortalContainer
    );

  return (
    <div className={cn("flex flex-col bg-white", outerClass, className)}>
      {headerTitle ? (
        <div
          className={cn(
            "flex items-center justify-between border-b px-2 py-1.5",
            headerClassName
          )}
        >
          <div className="flex-1">{headerTitle}</div>
          {showToolbar && (
            <EditorToolbar editor={editor} plain tooltipId={tooltipId} />
          )}
        </div>
      ) : (
        showToolbar && (
          <EditorToolbar editor={editor} tooltipId={tooltipId} />
        )
      )}

      {portalToolbar}

      <div className={sizingClass} style={sizingStyle}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default PharmacistTipTapEditor;
